import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Tag, Activity, Layers, Palette, ImageIcon, ArrowDownWideNarrow, LayoutGrid, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import AdCard from "@/components/library/AdCard";

const FILTER_CHIPS = [
  { key: "tags", Icon: Tag },
  { key: "activeAds", Icon: Activity },
  { key: "platforms", Icon: Layers },
  { key: "brands", Icon: Palette },
  { key: "asset", Icon: ImageIcon },
  { key: "sort", Icon: ArrowDownWideNarrow },
  { key: "display", Icon: LayoutGrid },
];

function groupByAd(items) {
  const map = new Map();
  for (const it of items) {
    const key = it.ad_id || `single-${it.id}`;
    if (!map.has(key)) map.set(key, { key, head: { ...it }, media: [it] });
    else {
      const g = map.get(key);
      g.media.push(it);
      if (g.head.media_type !== "image" && it.media_type === "image") {
        g.head = { ...g.head, thumbnail_url: it.thumbnail_url || it.file_url };
      }
      if (new Date(it.created_at) > new Date(g.head.created_at)) {
        g.head = { ...g.head, created_at: it.created_at };
      }
    }
  }
  return Array.from(map.values());
}

export default function Dashboard() {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["downloads", { limit: 200 }],
    queryFn: async () => (await api.get("/downloads", { params: { limit: 200 } })).data,
  });

  const purgeMutation = useMutation({
    mutationFn: async () => (await api.post("/downloads/purge")).data,
    onSuccess: (r) => {
      toast.success(`Library limpa (${r.deleted} registros removidos).`);
      qc.invalidateQueries({ queryKey: ["downloads"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
    onError: (e) => toast.error(e.message || "Falha ao limpar."),
  });

  const ads = useMemo(() => {
    const items = data?.items || [];
    const grouped = groupByAd(items);
    if (!q.trim()) return grouped;
    const term = q.trim().toLowerCase();
    return grouped.filter((g) => {
      const blob = [
        g.head.page_name,
        g.head.file_name,
        g.head.metadata?.headline,
        g.head.metadata?.primary_text,
        g.head.metadata?.cta,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(term);
    });
  }, [data, q]);

  return (
    <div className="space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground flex items-center gap-1.5">
          <span className="h-4 w-4 rounded-md bg-secondary grid place-items-center">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          </span>
          {t("nav.dashboard")}
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
          {t("library.tagline")}
        </h1>
      </header>

      <div
        className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 flex-wrap"
        data-testid="library-toolbar"
      >
        <div className="relative flex-1 min-w-[220px]">
          <Search strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9 border-0 bg-transparent focus-visible:ring-0 shadow-none h-9"
            placeholder={t("library.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="library-search"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {FILTER_CHIPS.map(({ key, Icon }) => (
            <Button
              key={key}
              size="sm"
              variant="ghost"
              className="h-8 rounded-md text-[12px] font-medium text-muted-foreground hover:text-foreground"
              data-testid={`library-chip-${key}`}
            >
              <Icon strokeWidth={1.5} className="h-3.5 w-3.5 mr-1.5" />
              {t(`library.${key}`)}
            </Button>
          ))}
          <Button
            size="sm"
            variant="ghost"
            className="h-8 rounded-md text-[12px] font-medium text-destructive/70 hover:text-destructive hover:bg-destructive/10"
            data-testid="library-purge-btn"
            disabled={purgeMutation.isPending || !data?.items?.length}
            onClick={() => {
              if (window.confirm("Isso apaga TODOS os anúncios salvos e não pode ser desfeito. Recomendado depois de atualizar a extensão para v1.5.0+ para limpar dados poluídos de versões antigas. Continuar?")) {
                purgeMutation.mutate();
              }
            }}
          >
            <Trash2 strokeWidth={1.5} className="h-3.5 w-3.5 mr-1.5" />
            Limpar tudo
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 2xl:columns-5 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="mb-4 h-64 w-full break-inside-avoid" />
          ))}
        </div>
      ) : ads.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-border p-16 text-center text-sm text-muted-foreground"
          data-testid="library-empty"
        >
          {t("library.empty")}
        </div>
      ) : (
        <div
          className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 xl:columns-5 2xl:columns-6 gap-4"
          data-testid="library-grid"
        >
          {ads.map((ad) => (
            <AdCard key={ad.key} ad={ad} />
          ))}
        </div>
      )}
    </div>
  );
}
